<template>
  <div
    class="flex items-center gap-3 rounded-theme-md border border-theme-border bg-theme-surface px-3 py-2"
  >
    <!-- Status dot -->
    <span
      :class="[
        'shrink-0 inline-block h-2 w-2 rounded-full',
        active ? 'bg-theme-success' : 'bg-theme-text-muted',
      ]"
      :aria-label="active ? t('ui.alert_row_active') : t('ui.alert_row_paused')"
    />

    <!-- Title + dataset -->
    <span class="flex-1 min-w-0">
      <span class="block truncate text-sm text-theme-text">
        {{ title }}
      </span>
      <span v-if="dataset || threshold" class="block truncate text-xs text-theme-text-muted">
        <template v-if="dataset">{{ dataset }}</template>
        <template v-if="dataset && threshold"> · </template>
        <template v-if="threshold">{{ threshold }}</template>
      </span>
    </span>

    <!-- Last triggered -->
    <span v-if="lastTriggeredLabel" class="hidden shrink-0 tabular-nums text-[11px] text-theme-text-muted sm:inline">
      {{ lastTriggeredLabel }}
    </span>

    <!-- Delivery indicator -->
    <span v-if="delivery" class="hidden shrink-0 text-[10px] uppercase tracking-wide text-theme-text-muted md:inline">
      {{ delivery }}
    </span>

    <!-- Toggle (active/paused) -->
    <button
      type="button"
      :class="[
        'shrink-0 inline-flex items-center justify-center w-9 h-5 rounded-full transition-colors',
        active ? 'bg-theme-success' : 'bg-theme-bg-subtle',
      ]"
      :aria-pressed="active"
      @click.stop="$emit('toggle', !active)"
    >
      <span
        :class="[
          'inline-block h-4 w-4 transform rounded-full bg-theme-text-inverse shadow transition-transform',
          active ? 'translate-x-2' : '-translate-x-2',
        ]"
      />
    </button>

    <!-- Edit -->
    <AppButton
      type="button"
      variant="ghost"
      tone="muted"
      size="xs"
      icon-only
      :icon="mdiPencilOutline"
      :aria-label="t('ui.alert_row_edit')"
      @click.stop="$emit('edit')"
    />

    <!-- Delete -->
    <AppButton
      type="button"
      variant="ghost"
      tone="danger"
      size="xs"
      icon-only
      :icon="mdiTrashCanOutline"
      :aria-label="t('ui.alert_row_delete')"
      @click.stop="$emit('delete')"
    />
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import { mdiPencilOutline, mdiTrashCanOutline } from '@mdi/js';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps({
    title: { type: String, required: true },
    dataset: { type: String, default: '' },
    threshold: { type: String, default: '' },
    delivery: { type: String, default: '' },
    active: { type: Boolean, default: true },
    lastTriggered: { type: [String, Date, null], default: null },
  });

  defineEmits(['toggle', 'edit', 'delete']);
  const { t } = useI18n();

  const lastTriggeredLabel = computed(() => {
    if (!props.lastTriggered) return '';
    const d = new Date(props.lastTriggered);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString();
  });
</script>
